import { useState, useEffect, useCallback } from 'react';

type FormType = 'pupil' | 'community';

const helpOptions = [
  { value: 'events', label: 'Helping at events' },
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'liturgy', label: 'Mass & liturgy' },
  { value: 'memorabilia', label: 'Sharing photos & memories' },
];

function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', top: 100, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, padding: '1rem 2rem', borderRadius: 2,
      background: type === 'success' ? 'var(--navy-deep)' : 'var(--burgundy)',
      color: 'var(--cream)', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem',
      animation: 'toastIn 0.4s ease',
      maxWidth: '90vw', textAlign: 'center',
    }}>
      {message}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default function Register() {
  const [tab, setTab] = useState<FormType>('pupil');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const clearToast = useCallback(() => setToast(null), []);

  const handleSubmit = async (type: FormType) => {
    const prefix = type === 'pupil' ? 'p' : 'c';
    const getVal = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value?.trim();
    const getChecked = (id: string) => (document.getElementById(id) as HTMLInputElement)?.checked;

    const fname = getVal(`${prefix}-fname`);
    const lname = getVal(`${prefix}-lname`);
    const email = getVal(`${prefix}-email`);
    const phone = getVal(`${prefix}-phone`);
    const consent = getChecked(`${prefix}-consent`);

    if (!fname || !email || !phone) {
      setToast({ message: 'Please fill in at least your name, email and phone number.', type: 'error' });
      return;
    }

    if (type === 'community') {
      const role = (document.getElementById('c-role') as HTMLSelectElement)?.value;
      if (!role) {
        setToast({ message: 'Please let us know how you are connected to the school.', type: 'error' });
        return;
      }
    }

    if (!consent) {
      setToast({ message: 'Please tick the consent box to confirm you are happy to share your details.', type: 'error' });
      return;
    }

    setSubmitting(true);

    const yearLeft = type === 'pupil' ? getVal('p-year') : null;
    const role = type === 'community' ? (document.getElementById('c-role') as HTMLSelectElement)?.value : null;

    const helpCheckboxes = document.querySelectorAll<HTMLInputElement>(`input[name="help"]:checked`);
    const helpWith = Array.from(helpCheckboxes).map(cb => cb.value);

    try {
      const res = await fetch('/api/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: type,
          firstName: fname,
          lastName: lname,
          email,
          phone,
          yearLeft,
          role,
          helpWith,
          consent: true,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToast({ message: data.message || 'Thank you! Your registration has been submitted.', type: 'success' });
        const form = document.getElementById(`tab-${type}`);
        if (form) {
          const inputs = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select');
          inputs.forEach(inp => {
            if (inp.type === 'checkbox') inp.checked = false;
            else inp.value = '';
          });
        }
      } else {
        setToast({ message: data.error || 'Something went wrong. Please try again.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error. Please check your connection and try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (id: string, label: string, placeholder: string, type: string = 'text') => (
    <div className="field" style={{ marginBottom: '1.3rem' }}>
      <label htmlFor={id} style={{
        display: 'block', fontSize: '0.72rem', letterSpacing: '0.2em',
        textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '0.5rem', fontWeight: 500,
      }}>{label}</label>
      <input type={type} id={id} placeholder={placeholder} style={{
        width: '100%', padding: '0.85rem 0', border: 'none',
        borderBottom: '1px solid rgba(15, 29, 58, 0.25)',
        background: 'transparent',
        fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem',
        color: 'var(--ink)', outline: 'none', transition: 'border-color 0.3s',
      }} />
    </div>
  );

  const renderHelpCheckboxes = () => (
    <div className="field" style={{ marginBottom: '1.3rem' }}>
      <label style={{
        display: 'block', fontSize: '0.72rem', letterSpacing: '0.2em',
        textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '0.5rem', fontWeight: 500,
      }}>I&apos;d like to help with… <span className="optional" style={{
        fontSize: '0.7rem', fontStyle: 'italic', textTransform: 'none',
        letterSpacing: '0.05em', color: '#999',
        fontFamily: "'Cormorant Garamond', serif", marginLeft: '0.3rem',
      }}>(optional)</span></label>
      <div className="checkbox-group" style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', marginTop: '0.5rem',
      }}>
        {helpOptions.map(opt => (
          <label key={opt.value} className="check" style={{
            display: 'flex', alignItems: 'center', gap: '0.55rem',
            cursor: 'pointer', fontFamily: "'Cormorant Garamond', serif",
            fontSize: '1rem', color: '#3a3a3a', padding: '0.3rem 0',
          }}>
            <input type="checkbox" name="help" value={opt.value} className="help-cb" />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <section className="register" id="register" style={{
      background: 'var(--cream-warm)', padding: '7rem 3rem',
      position: 'relative', overflow: 'hidden',
    }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}

      <div style={{
        position: 'absolute', top: -20, right: -30,
        fontFamily: "'Fraunces', serif", fontWeight: 900, fontStyle: 'italic',
        fontSize: '14rem', color: 'rgba(122, 29, 43, 0.05)',
        lineHeight: 0.8, pointerEvents: 'none',
      }}>MCMLXXVI</div>

      <div className="register-inner" style={{
        maxWidth: 1100, margin: '0 auto',
        display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '4rem',
        alignItems: 'center', position: 'relative', zIndex: 2,
      }}>
        <div className="register-text">
          <div className="section-eyebrow">Get Involved</div>
          <h2 className="section-title">Register your interest and we&apos;ll <em>keep you posted.</em></h2>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem',
            lineHeight: 1.6, color: '#3a3a3a', marginBottom: '1rem',
          }}>
            Whether you&apos;re a past pupil, a current parent, a parishioner or a neighbour — leave your details and we&apos;ll be in touch with everything you need to know about the year&apos;s events. Dates, invitations, ticket releases, volunteer opportunities and ways to support your school.
          </p>
          <ul className="register-list" style={{ listStyle: 'none', marginTop: '1.5rem' }}>
            {[
              'Priority access to Gala Ball tickets',
              'Invitations to class-year and parish gatherings',
              'News on community days and Mass celebrations',
              'Chances to volunteer, help organise or fundraise',
            ].map((item, i) => (
              <li key={i} style={{
                padding: '0.6rem 0', borderBottom: '1px solid rgba(122, 29, 43, 0.15)',
                fontSize: '0.95rem', color: 'var(--navy)',
                display: 'flex', alignItems: 'center', gap: '1rem',
              }}>
                <span style={{ color: 'var(--burgundy)', fontSize: '0.8rem' }}>✦</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="form-card" style={{
          background: 'var(--cream)', padding: '3rem 2.5rem',
          border: '1px solid rgba(15, 29, 58, 0.1)',
          position: 'relative', boxShadow: '0 30px 60px rgba(15, 29, 58, 0.08)',
        }}>
          <div style={{
            position: 'absolute', inset: 8, border: '1px solid var(--gold)',
            pointerEvents: 'none', opacity: 0.5,
          }} />

          {/* Tabs */}
          <div className="form-tabs" role="tablist" style={{
            display: 'flex', gap: 0, margin: '-0.5rem -0.5rem 2rem',
            borderBottom: '1px solid rgba(15, 29, 58, 0.15)',
          }}>
            {(['pupil', 'community'] as FormType[]).map(t => (
              <button key={t} type="button" className={`tab ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)} role="tab"
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  padding: '0.9rem 0.5rem',
                  fontFamily: "'Inter', sans-serif", fontSize: '0.72rem',
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: tab === t ? 'var(--burgundy)' : 'rgba(15, 29, 58, 0.5)',
                  cursor: 'pointer', fontWeight: 500, position: 'relative',
                  margin: 0, transition: 'color 0.3s',
                }}>
                {t === 'pupil' ? 'Past Pupil' : 'Parent & Community'}
              </button>
            ))}
          </div>

          {/* PAST PUPIL FORM */}
          <div id="tab-pupil" style={{ display: tab === 'pupil' ? 'block' : 'none' }}>
            <h3 style={{
              fontFamily: "'Fraunces', serif", fontWeight: 500,
              fontSize: '1.6rem', color: 'var(--navy)', marginBottom: '0.4rem', textAlign: 'center',
            }}>Past Pupil Registration</h3>
            <p className="form-sub" style={{
              fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
              textAlign: 'center', color: 'var(--burgundy)', marginBottom: '2rem', fontSize: '1.05rem',
            }}>Welcome home.</p>
            <div className="field-row" style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem',
            }}>
              {renderField('p-fname', 'First Name', 'Mary')}
              {renderField('p-lname', 'Surname', "O'Sullivan")}
            </div>
            {renderField('p-year', 'Year you left school', 'e.g. 1994')}
            {renderField('p-email', 'Email Address', 'mary@example.ie', 'email')}
            {renderField('p-phone', 'Phone Number', '+353 ...', 'tel')}
            {renderHelpCheckboxes()}
            <div className="field-consent" style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
              marginTop: '1.5rem', marginBottom: '0.5rem',
              padding: '0.9rem 1rem', background: 'rgba(122, 29, 43, 0.04)',
              borderLeft: '2px solid var(--burgundy)',
            }}>
              <input type="checkbox" id="p-consent" className="consent-cb" />
              <label htmlFor="p-consent" style={{
                fontFamily: "'Cormorant Garamond', serif", fontSize: '1rem',
                lineHeight: 1.4, color: '#3a3a3a', cursor: 'pointer', fontStyle: 'italic',
              }}>I consent to providing my information to the Queen of Angels Past Pupils Association.</label>
            </div>
            <button type="button" disabled={submitting} onClick={() => handleSubmit('pupil')} style={{
              width: '100%', padding: '1rem',
              background: submitting ? '#999' : 'var(--burgundy)',
              color: 'var(--cream)', border: 'none',
              fontFamily: "'Inter', sans-serif", fontWeight: 500,
              fontSize: '0.82rem', letterSpacing: '0.25em',
              textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '1rem', transition: 'all 0.3s',
            }}>
              {submitting ? 'Submitting…' : 'Register My Details'}
            </button>
          </div>

          {/* COMMUNITY FORM */}
          <div id="tab-community" style={{ display: tab === 'community' ? 'block' : 'none' }}>
            <h3 style={{
              fontFamily: "'Fraunces', serif", fontWeight: 500,
              fontSize: '1.6rem', color: 'var(--navy)', marginBottom: '0.4rem', textAlign: 'center',
            }}>Parent & Community Registration</h3>
            <p className="form-sub" style={{
              fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
              textAlign: 'center', color: 'var(--burgundy)', marginBottom: '2rem', fontSize: '1.05rem',
            }}>Be part of the year ahead.</p>
            <div className="field-row" style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem',
            }}>
              {renderField('c-fname', 'First Name', 'Seán')}
              {renderField('c-lname', 'Surname', 'Murphy')}
            </div>
            <div className="field" style={{ marginBottom: '1.3rem' }}>
              <label htmlFor="c-role" style={{
                display: 'block', fontSize: '0.72rem', letterSpacing: '0.2em',
                textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '0.5rem', fontWeight: 500,
              }}>I am a…</label>
              <select id="c-role" style={{
                width: '100%', padding: '0.85rem 0', border: 'none',
                borderBottom: '1px solid rgba(15, 29, 58, 0.25)',
                background: 'transparent',
                fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem',
                color: 'var(--ink)', outline: 'none', transition: 'border-color 0.3s',
                cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none',
              }}>
                <option value="">Please choose…</option>
                <option value="parent">Current parent / guardian</option>
                <option value="parishioner">Member of the parish</option>
                <option value="community">Local community member</option>
                <option value="staff">Current or former staff</option>
                <option value="other">Other / friend of the school</option>
              </select>
            </div>
            {renderField('c-email', 'Email Address', 'sean@example.ie', 'email')}
            {renderField('c-phone', 'Phone Number', '+353 ...', 'tel')}
            {renderHelpCheckboxes()}
            <div className="field-consent" style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
              marginTop: '1.5rem', marginBottom: '0.5rem',
              padding: '0.9rem 1rem', background: 'rgba(122, 29, 43, 0.04)',
              borderLeft: '2px solid var(--burgundy)',
            }}>
              <input type="checkbox" id="c-consent" className="consent-cb" />
              <label htmlFor="c-consent" style={{
                fontFamily: "'Cormorant Garamond', serif", fontSize: '1rem',
                lineHeight: 1.4, color: '#3a3a3a', cursor: 'pointer', fontStyle: 'italic',
              }}>I consent to providing my information to Queen of Angels Primary School for the purpose of the 50th Anniversary celebrations.</label>
            </div>
            <button type="button" disabled={submitting} onClick={() => handleSubmit('community')} style={{
              width: '100%', padding: '1rem',
              background: submitting ? '#999' : 'var(--burgundy)',
              color: 'var(--cream)', border: 'none',
              fontFamily: "'Inter', sans-serif", fontWeight: 500,
              fontSize: '0.82rem', letterSpacing: '0.25em',
              textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '1rem', transition: 'all 0.3s',
            }}>
              {submitting ? 'Submitting…' : 'Register My Interest'}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 960px) {
          .register-inner { grid-template-columns: 1fr !important; gap: 3rem !important; }
          .field-row { grid-template-columns: 1fr !important; gap: 0 !important; }
          .form-card { padding: 2rem 1.5rem !important; }
          .checkbox-group { grid-template-columns: 1fr !important; }
          .form-tabs .tab { font-size: 0.65rem !important; letter-spacing: 0.12em !important; }
        }
      `}</style>
    </section>
  );
}
